"use client";

import dynamic from "next/dynamic";
import type { ReactNode } from "react";
import BirthToolFormShell from "@/components/tool-shell/BirthToolFormShell";
import { useDeferredToolActivation } from "@/components/tool-shell/useDeferredToolActivation";

// Bundle-size discipline (2026-07-16 trim pass): the interactive island
// (BirthDetailsForm + PlaceAutocomplete + NAKSHATRA_DATA + result stack) lives
// in ./NakshatraCalculatorClient and loads via next/dynamic, sharing ONE copy
// of the form stack with the other deferred calculator islands (and one copy
// of NAKSHATRA_DATA across the en + hi routes). The header/H1 + SEO prose
// render immediately (rules 43/61); the shell reserves the swap height (rule 58).
const NakshatraToolIsland = dynamic(() => import("./NakshatraCalculatorClient"), {
  ssr: false,
  loading: () => <BirthToolFormShell />,
});

export default function DeferredNakshatraCalculatorClient({
  defaultLanguage = "en",
  crossLinks,
}: {
  defaultLanguage?: "en" | "hi";
  crossLinks: ReactNode;
}) {
  const isHindi = defaultLanguage === "hi";
  const { ready, activate } = useDeferredToolActivation();

  return (
    <div className="sacred-bg min-h-[calc(100vh-130px)]">
      <div className="mx-auto max-w-2xl px-4 sm:px-6 py-12 sm:py-16">
        <NakshatraHeader isHindi={isHindi} />

        {ready ? (
          <NakshatraToolIsland defaultLanguage={defaultLanguage} />
        ) : (
          <BirthToolFormShell
            language={defaultLanguage}
            submitLabel={isHindi ? "मेरा नक्षत्र बताएं" : "Find My Nakshatra"}
            onActivate={activate}
          />
        )}

        <NakshatraSeoContent />

        {crossLinks}
      </div>
    </div>
  );
}

function NakshatraHeader({ isHindi }: { isHindi: boolean }) {
  return (
    <div className="text-center mb-10">
      <p
        className="text-[11px] font-semibold uppercase tracking-[0.25em] mb-3"
        style={{ color: "var(--ish-accent)" }}
      >
        {isHindi ? "नक्षत्र गणना" : "Nakshatra Ganana"}
      </p>
      <h1
        className="text-[28px] sm:text-[36px] font-bold leading-tight"
        style={{
          fontFamily: "var(--font-pt-serif), PT Serif, Georgia, serif",
          color: "var(--ish-dark)",
        }}
      >
        {isHindi ? "निःशुल्क नक्षत्र कैलकुलेटर" : "Free Nakshatra Calculator"}
      </h1>
      <p className="mt-3 text-[15px] leading-relaxed max-w-md mx-auto text-ish-text-muted">
        {isHindi
          ? "अपनी जन्म तिथि दर्ज करें और तुरंत अपना जन्म नक्षत्र, पाद, स्वामी ग्रह, देवता और नामाक्षर जानें — 100% निःशुल्क।"
          : "Enter your date of birth and find your Janma Nakshatra (birth star), pada, ruling planet, deity, and name syllable — 100% free and instant."}
      </p>

      <div
        className="mt-5 flex flex-wrap justify-center gap-x-5 gap-y-2 text-[11px] font-medium"
        style={{ color: "var(--ish-text-faint)" }}
      >
        <TrustSignal
          icon="shield"
          label={isHindi ? "सटीक वैदिक गणना" : "Accurate Vedic calculations"}
        />
        <TrustSignal icon="clock" label={isHindi ? "तुरंत परिणाम" : "Instant Results"} />
        <TrustSignal icon="user" label={isHindi ? "100% निःशुल्क" : "100% Free"} />
      </div>
    </div>
  );
}

function TrustSignal({ icon, label }: { icon: "shield" | "clock" | "user"; label: string }) {
  return (
    <span className="flex items-center gap-1.5">
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        {icon === "shield" && <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />}
        {icon === "clock" && (
          <>
            <circle cx="12" cy="12" r="10" />
            <path d="M12 6v6l4 2" />
          </>
        )}
        {icon === "user" && (
          <>
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </>
        )}
      </svg>
      {label}
    </span>
  );
}

function NakshatraSeoContent() {
  return (
    <div className="mt-12 card-sacred p-6 sm:p-8">
      <h2
        className="text-[20px] font-bold mb-4"
        style={{
          fontFamily: "var(--font-pt-serif), PT Serif, Georgia, serif",
          color: "var(--ish-dark)",
        }}
      >
        What is a Nakshatra?
      </h2>
      <div className="space-y-3 text-[14px] leading-relaxed text-ish-text-muted">
        <p>
          <strong style={{ color: "var(--ish-dark)" }}>Nakshatras</strong> are the 27 lunar mansions
          in Vedic astrology. Each nakshatra spans 13 degrees and 20 minutes of the zodiac, and is
          further divided into 4 padas (quarters) of 3 degrees 20 minutes each. Your Janma Nakshatra
          (birth star) is determined by the Moon&apos;s position at your exact time of birth.
        </p>
        <p>
          Each nakshatra has a ruling planet (graha), a presiding deity (devata), and unique
          characteristics that influence your personality, temperament, and life path. The nakshatra
          system is fundamental to Vedic astrology and is used for muhurta (auspicious timing),
          compatibility matching, dasha calculations, and predictive astrology.
        </p>
        <p>
          The 27 nakshatras begin with Ashwini and end with Revati, covering the complete 360-degree
          zodiac. Knowing your nakshatra and pada is essential for accurate Vedic astrological
          analysis.
        </p>
      </div>
    </div>
  );
}
